import { ChevronDown, Menu } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { sanitizeRedirectPath } from '../../utils/redirectAfterLogin';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { btnPrimarySm, focusRing } from '../ui/redesign';

const AUTH_PAGES = ['/login', '/register', '/confirm-email', '/forgot-password', '/reset-password'];

/**
 * Site header (docs/prd-homepage-redesign.md §8.1, prototype `.hdr`): warm-white sticky
 * bar, three main links, a hamburger menu on phones. The signed-in menu keeps the
 * existing entries (My Home, host dashboard, admin, log out).
 */
export default function Header() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // A host (or admin) is already a host: no "Become a host" link for them.
  const isHost = !!user && (user.isHost || user.isAdmin);
  const navItems = [
    { to: '/activities', label: t('nav.activities') },
    { to: '/picks', label: t('nav.picks') },
    ...(!isHost ? [{ to: '/apply-host', label: t('nav.becomeHost') }] : []),
  ];
  const mobileItems = [...navItems, { to: '/gift-cards', label: t('nav.giftCards') }];

  // Close menus on navigation.
  useEffect(() => {
    setMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch {
      // Logout errors are handled inside logout(); the user stays where they are.
    }
  };

  // On the sign-in pages themselves, keep the return target they already carry.
  const onAuthPage = AUTH_PAGES.some((p) => location.pathname.startsWith(p));
  const returnTo = onAuthPage
    ? sanitizeRedirectPath(new URLSearchParams(location.search).get('redirect'))
    : `${location.pathname}${location.search}${location.hash}`;
  const loginHref = returnTo ? `/login?redirect=${encodeURIComponent(returnTo)}` : '/login';
  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();
  const displayName = user?.name || user?.email?.split('@')[0] || '';

  const menuLink =
    'block px-4 py-2 font-text text-sm text-ink hover:bg-warm-white transition-colors';

  return (
    <header className="sticky top-[env(safe-area-inset-top,0px)] z-40 bg-[rgba(250,246,241,0.94)] shadow-[0_6px_18px_-14px_rgba(60,50,30,0.45)] font-ui backdrop-blur-[10px]">
      <div className="mx-auto flex h-[60px] max-w-[1160px] items-center gap-2 px-4 desk:h-[68px] desk:gap-[22px] desk:px-7">
        <button
          type="button"
          className="-ml-1.5 grid place-items-center p-2 text-ink desk:hidden"
          aria-label={t('nav.menu')}
          aria-expanded={menuOpen}
          aria-controls="site-mobile-nav"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <Menu className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>

        <Link
          to="/"
          aria-label={t('nav.home')}
          className="inline-flex shrink-0 items-center gap-2.5 whitespace-nowrap text-[18px] font-bold text-primary desk:text-[21px]"
        >
          <img src="/logo.svg" alt="" className="h-[30px] w-[30px] desk:h-9 desk:w-9" />
          <span>YOYO Club</span>
        </Link>

        <nav className="hidden gap-[22px] desk:flex" aria-label={t('nav.menu')}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `py-1.5 text-[15px] font-medium transition-colors hover:text-primary ${
                  isActive ? 'text-primary' : 'text-ink-2'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        <LanguageSwitcher variant="pill" />

        {isAuthenticated ? (
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              aria-label={t('nav.greeting', { name: user?.name || user?.email })}
              className={`flex h-[38px] shrink-0 items-center gap-1.5 rounded-full border-[1.5px] border-line bg-white py-0.5 pl-0.5 pr-2 font-ui text-[13px] font-semibold text-ink-2 transition-colors hover:border-primary hover:text-primary desk:gap-2 desk:pr-3 ${focusRing} ${
                userMenuOpen ? 'border-primary text-primary' : ''
              }`}
            >
              {/* The live site's "Hi, name" with a chevron, in the redesigned pill look. */}
              <span className="grid h-[30px] w-[30px] place-items-center rounded-full bg-primary text-[13px] font-semibold text-white">
                {initial}
              </span>
              <span className="hidden max-w-[140px] truncate desk:inline">
                {t('nav.greeting', { name: displayName })}
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {userMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 z-50 mt-2 w-52 rounded-[14px] border border-line bg-white py-2 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.45)]"
              >
                <div className="truncate px-4 pb-2 font-text text-xs text-mute">
                  {user?.name || user?.email}
                </div>
                <Link role="menuitem" to="/dashboard" className={menuLink}>
                  {t('nav.myHome')}
                </Link>
                {!user?.isHost && !user?.isAdmin && (
                  <Link role="menuitem" to="/apply-host" className={menuLink}>
                    {t('nav.becomeHost')}
                  </Link>
                )}
                {user?.isHost && !user?.isAdmin && (
                  <Link role="menuitem" to="/host/activities" className={menuLink}>
                    Host Dashboard
                  </Link>
                )}
                {user?.isAdmin && (
                  <Link role="menuitem" to="/admin/activities" className={menuLink}>
                    {t('nav.adminPanel')}
                  </Link>
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    void handleLogout();
                  }}
                  className={`${menuLink} w-full text-left`}
                >
                  {t('nav.logout')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link to={loginHref} className={`${btnPrimarySm} shrink-0 whitespace-nowrap`}>
            {t('nav.login')}
          </Link>
        )}
      </div>

      {menuOpen && (
        <nav
          id="site-mobile-nav"
          aria-label={t('nav.menu')}
          className="bg-warm-white pb-2 desk:hidden"
        >
          {mobileItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="block w-full px-4 py-3 text-left font-medium text-ink"
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
